import CompanyAddressInformation from "@/components/Company/CompanyAddressInformation.tsx";
import ProviderCompanyAutocomplete from "@/components/Forms/ProviderCompanyAutocomplete.tsx";
import ProviderSelectedMedia from "@/components/Forms/ProviderSelectedMedia.tsx";
import ProviderSelectedRecord from "@/components/Forms/ProviderSelectedRecord.tsx";
import useGetFormValueAsNumber from "@/components/Forms/hook/useGetFormValueAsNumber.tsx";
import { addProviderSchema } from "@/components/Forms/schema/addProviderSchema.ts";
import { type NewCompany, useAddCompany } from "@/mutations/useAddCompany.tsx";
import { useAddProviderToCase } from "@/mutations/useAddProviderToCase.tsx";
import { type Media, useFetchMediasForCase } from "@/queries/useFetchMediaForCase.ts";
import { useFetchParty } from "@/queries/useFetchParty.ts";
import { getErrorMessage } from "@/utils/api.ts";
import { localDateToDayjs } from "@/utils/datetime.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import type { LocalDate } from "@js-joda/core";
import {
    Alert,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid2 as Grid,
    Link,
    MenuItem,
    Typography,
} from "@mui/material";
import { RhfTextField } from "mui-rhf-integration";
import { RhfDatePicker } from "mui-rhf-integration/date-picker";
import { enqueueSnackbar } from "notistack";
import { type BaseSyntheticEvent, type ReactNode, useCallback, useEffect, useState } from "react";
import { FormProvider, type SubmitHandler, useForm } from "react-hook-form";
import type { z } from "zod";

export const mediaJsonToValue = (selectedMedia: string | null) => {
    if (selectedMedia) {
        try {
            const selectedMediaJson = JSON.parse(selectedMedia);

            if (selectedMediaJson.fieldData.id_UUID) {
                return selectedMediaJson.fieldData.id_UUID;
            }

            if (selectedMediaJson.fieldData.id_UUID) {
                return selectedMediaJson.fieldData.id_UUID;
            }
        } catch (e) {
            console.error("error parsing selectedMedia", e);
        }
    }

    return "";
};

export const mediaJsonToFileName = (selectedMedia: string | null) => {
    if (selectedMedia) {
        try {
            const selectedMediaJson = JSON.parse(selectedMedia);

            if (selectedMediaJson.fieldData.Filename) {
                return selectedMediaJson.fieldData.Filename;
            }
        } catch (e) {
            console.error("error parsing selectedMedia", e);
        }
    }

    return "";
};

export const valueToMediaJson = (
    mediaUUID: string | null | undefined,
    media: Media[] | undefined,
) => {
    if (mediaUUID && media) {
        const foundMedia = media.find((m) => m.idUUID === mediaUUID);

        if (foundMedia) {
            return JSON.stringify({
                fieldData: {
                    id_UUID: foundMedia.idUUID,
                    Filename: foundMedia.filename,
                },
            });
        }
    }

    return "";
};

const massageLocalDateToDayjsDates = (data: Record<string, unknown>) => {
    return {
        ...data,
        providerStartDate: data.providerStartDate
            ? localDateToDayjs(data.providerStartDate as LocalDate | string)
            : null,
        providerEndDate: data.providerEndDate
            ? localDateToDayjs(data.providerEndDate as LocalDate | string)
            : null,
        providerBalance: data.providerBalance ? data.providerBalance.toString() : null,
        providerBill: data.providerBill ? data.providerBill.toString() : null,
        providerHealthInsPmt: data.providerHealthInsPmt
            ? data.providerHealthInsPmt.toString()
            : null,
        providerSelfPay: data.providerSelfPay ? data.providerSelfPay.toString() : null,
        providerMedPay: data.providerMedPay ? data.providerMedPay.toString() : null,
        providerClientToPay: data.providerClientToPay ? data.providerClientToPay.toString() : null,
        providerReduction: data.providerReduction ? data.providerReduction.toString() : null,
        providerAdjustment: data.providerAdjustment ? data.providerAdjustment.toString() : null,
        selectedMedia: mediaJsonToValue(data.selectedMedia as string | null),
        selectedRecord: mediaJsonToValue(data.selectedRecord as string | null),
    };
};

export type AddProviderFormValues = z.infer<typeof addProviderSchema>;

type Props = {
    caseUUID: string;
    showAddButton?: boolean;
    editPartyId?: string;
    clearEditPartyId?: () => void;
};

const AddEditProviderDialog = ({
    caseUUID,
    editPartyId,
    clearEditPartyId,
    showAddButton = true,
}: Props): ReactNode => {
    const addProviderToCaseMutation = useAddProviderToCase(caseUUID);
    const addCompanyMutation = useAddCompany();
    const [open, setOpen] = useState(false);
    const [isAddingProvider, setIsAddingProvider] = useState(false);

    const { data: media } = useFetchMediasForCase({ caseIdUUID: caseUUID });

    const {
        data: { data } = {},
        isError,
        isRefetching,
        isLoading,
    } = useFetchParty({
        partyId: editPartyId ?? null,
        isQueryEnabled: editPartyId !== undefined,
    });

    const form = useForm<AddProviderFormValues>({
        resolver: zodResolver(addProviderSchema),
        defaultValues: {},
    });

    const providerBillWatch = useGetFormValueAsNumber(form.watch("providerBill"));
    const providerHealthInsPmtWatch = useGetFormValueAsNumber(form.watch("providerHealthInsPmt"));
    const providerAdjustmentWatch = useGetFormValueAsNumber(form.watch("providerAdjustment"));
    const providerSelfPayWatch = useGetFormValueAsNumber(form.watch("providerSelfPay"));
    const providerMedPayWatch = useGetFormValueAsNumber(form.watch("providerMedPay"));
    const providerReductionWatch = useGetFormValueAsNumber(form.watch("providerReduction"));
    const providerClientToPayWatch = useGetFormValueAsNumber(form.watch("providerClientToPay"));

    const balance =
        (Math.round((providerBillWatch ?? 0) * 100) -
            Math.round((providerHealthInsPmtWatch ?? 0) * 100) -
            Math.round((providerAdjustmentWatch ?? 0) * 100) -
            Math.round((providerSelfPayWatch ?? 0) * 100) -
            Math.round((providerMedPayWatch ?? 0) * 100) -
            Math.round((providerReductionWatch ?? 0) * 100) -
            Math.round((providerClientToPayWatch ?? 0) * 100)) /
        100;

    const handleSubmit = useCallback(
        (values: AddProviderFormValues) => {
            addProviderToCaseMutation.mutate(
                {
                    ...values,
                    selectedMedia: valueToMediaJson(values.selectedMedia, media),
                    selectedRecord: valueToMediaJson(values.selectedRecord, media),
                },
                {
                    onSuccess: () => {
                        enqueueSnackbar("Provider added.", { variant: "success" });
                        if (clearEditPartyId) {
                            clearEditPartyId();
                        }

                        form.reset({});
                        setOpen(false);
                        setIsAddingProvider(false);
                    },
                    onError: (error) => {
                        enqueueSnackbar("Error: Add provider failed.", { variant: "error" });
                        console.info("error", getErrorMessage(error));
                    },
                },
            );
        },
        [addProviderToCaseMutation, form.reset, media, clearEditPartyId],
    );

    const onSubmit: SubmitHandler<AddProviderFormValues> = async (
        data: AddProviderFormValues,
        event?: BaseSyntheticEvent,
    ) => {
        event?.stopPropagation();
        handleSubmit(data);
    };

    const handleShowAddProviderForm = useCallback(() => {
        form.setValue("idCompanyID", "");

        const companyFields = [
            "companyName",
            "address",
            "address2",
            "city",
            "state",
            "zip",
            "phoneNumber",
            "phoneExtension",
            "faxNumber",
            "email",
        ];
        companyFields.forEach((field) => {
            form.setValue(field as keyof AddProviderFormValues, "");
        });

        setIsAddingProvider(true);
    }, [form]);

    const handleSaveProvider = useCallback(() => {
        const values = form.getValues();

        return addCompanyMutation.mutate(
            {
                email: values.email ?? "",
                faxNumber: values.faxNumber ?? "",
                phoneNumber: values.phoneNumber ?? "",
                phoneExtension: values.phoneExtension ?? "",
                companyName: values.companyName ?? "",
                address: values.address ?? "",
                address2: values.address2 ?? "",
                city: values.city ?? "",
                state: values.state ?? "",
                zip: values.zip ?? "",
            },
            {
                onSuccess: (newCompany: NewCompany) => {
                    form.setValue("idCompanyID", newCompany.idUUID);
                    enqueueSnackbar("Provider company added successfully.", { variant: "success" });
                    setIsAddingProvider(false);
                },
                onError: (error) => {
                    enqueueSnackbar("Error: Add Provider failed.", { variant: "error" });
                    console.info("error", getErrorMessage(error));
                },
            },
        );
    }, [form, addCompanyMutation]);

    const handleCancelAddProvider = useCallback(() => {
        const companyFields = [
            "companyName",
            "address",
            "address2",
            "city",
            "state",
            "zip",
            "phoneNumber",
            "phoneExtension",
            "faxNumber",
            "email",
        ];
        companyFields.forEach((field) => {
            form.setValue(field as keyof AddProviderFormValues, "");
        });

        setIsAddingProvider(false);
    }, [form]);

    const handleCloseDialog = useCallback(() => {
        if (clearEditPartyId) {
            clearEditPartyId();
        }
        form.reset({});
        setOpen(false);
        setIsAddingProvider(false);
    }, [clearEditPartyId, form]);

    useEffect(() => {
        if (!Number.isNaN(balance)) {
            form.setValue("providerBalance", balance.toString());
        }
    }, [balance, form.setValue]);

    useEffect(() => {
        if (data) {
            form.reset(massageLocalDateToDayjsDates(data));
            setOpen(true);
        }
    }, [data]);

    return (
        <>
            {showAddButton && (
                <Button
                    variant={"contained"}
                    sx={{
                        ml: 2,
                        minWidth: "120px",
                    }}
                    onClick={() => {
                        form.reset({});
                        setOpen(true);
                    }}
                >
                    {editPartyId ? "Edit" : "New"} Provider
                </Button>
            )}
            <Dialog open={open} maxWidth="sm" fullWidth>
                <FormProvider {...form}>
                    <form>
                        <DialogTitle>{editPartyId ? "Edit" : "New"} Provider</DialogTitle>
                        <DialogContent dividers>
                            {(isLoading || isRefetching) && <CircularProgress />}
                            {isError && <Alert severity={"error"}>Error loading form data.</Alert>}
                            <Grid container spacing={2}>
                                <Grid size={12}>
                                    <ProviderCompanyAutocomplete form={form} />
                                    {!isAddingProvider && (
                                        <Link
                                            component="button"
                                            type="button"
                                            onClick={handleShowAddProviderForm}
                                            sx={{
                                                color: "#4CAF50",
                                                textDecoration: "underline",
                                                cursor: "pointer",
                                                fontSize: "14px",
                                                display: "block",
                                                mt: 1,
                                            }}
                                        >
                                            Add Provider
                                        </Link>
                                    )}
                                </Grid>
                                <Grid size={12}>
                                    <CompanyAddressInformation
                                        onSaveProvider={handleSaveProvider}
                                        onCancelAddProvider={handleCancelAddProvider}
                                        isAddingProvider={isAddingProvider}
                                    />
                                </Grid>
                                <Grid size={12}>
                                    <Typography>Dates of Treatment</Typography>
                                </Grid>
                                <Grid size={6}>
                                    <RhfDatePicker
                                        control={form.control}
                                        name={"providerStartDate"}
                                        label={"Start Date"}
                                        views={["year", "month", "day"]}
                                        sx={{
                                            width: "100%",
                                        }}
                                    />
                                </Grid>
                                <Grid size={6}>
                                    <RhfDatePicker
                                        control={form.control}
                                        name={"providerEndDate"}
                                        label={"End Date"}
                                        views={["year", "month", "day"]}
                                        sx={{
                                            width: "100%",
                                        }}
                                    />
                                </Grid>
                                <Grid size={12}>
                                    <Typography>Billing Information</Typography>
                                </Grid>
                                <Grid size={6}>
                                    <RhfTextField
                                        control={form.control}
                                        name={"providerBill"}
                                        label={"Bill Amount"}
                                        sx={{
                                            width: "100%",
                                        }}
                                    />
                                </Grid>
                                <Grid size={6}>
                                    <RhfTextField
                                        control={form.control}
                                        name={"providerHealthInsPmt"}
                                        label={"Health Ins Payment"}
                                        sx={{
                                            width: "100%",
                                        }}
                                    />
                                </Grid>
                                <Grid size={6}>
                                    <RhfTextField
                                        control={form.control}
                                        name={"providerAdjustment"}
                                        label={"Provider Adjustment"}
                                        sx={{
                                            width: "100%",
                                        }}
                                    />
                                </Grid>
                                <Grid size={6}>
                                    <RhfTextField
                                        control={form.control}
                                        name={"providerSelfPay"}
                                        label={"Self Pay"}
                                        sx={{
                                            width: "100%",
                                        }}
                                    />
                                </Grid>
                                <Grid size={6}>
                                    <RhfTextField
                                        control={form.control}
                                        name={"providerMedPay"}
                                        label={"MedPay"}
                                        sx={{
                                            width: "100%",
                                        }}
                                    />
                                </Grid>
                                <Grid size={6}>
                                    <RhfTextField
                                        control={form.control}
                                        name={"providerClientToPay"}
                                        label={"Client To Pay"}
                                        sx={{
                                            width: "100%",
                                        }}
                                    />
                                </Grid>
                                <Grid size={6}>
                                    <RhfTextField
                                        control={form.control}
                                        name={"providerReduction"}
                                        label={"Reduced By"}
                                        sx={{
                                            width: "100%",
                                        }}
                                    />
                                </Grid>
                                <Grid size={6}>
                                    <RhfTextField
                                        select
                                        control={form.control}
                                        name={"providerReductionApproved"}
                                        label={"Reduction Approved?"}
                                        sx={{
                                            width: "100%",
                                        }}
                                    >
                                        <MenuItem
                                            key={"providerReductionApproved-blank"}
                                            value={""}
                                        />
                                        <MenuItem
                                            key={"providerReductionApproved-yes"}
                                            value={"Yes"}
                                        >
                                            Yes
                                        </MenuItem>
                                        <MenuItem key={"providerReductionApproved-no"} value={"No"}>
                                            No
                                        </MenuItem>
                                    </RhfTextField>
                                </Grid>
                                <Grid size={6}>
                                    <RhfTextField
                                        control={form.control}
                                        name={"providerBalance"}
                                        label={"Balance"}
                                        sx={{
                                            width: "100%",
                                        }}
                                        disabled
                                    />
                                </Grid>
                                <Grid size={12}>
                                    <RhfTextField
                                        control={form.control}
                                        name={"note"}
                                        label={"Note"}
                                        sx={{
                                            width: "100%",
                                        }}
                                        multiline
                                        rows={4}
                                    />
                                </Grid>
                                <Grid size={12}>
                                    <ProviderSelectedMedia form={form} caseIdUUID={caseUUID} />
                                </Grid>
                                <Grid size={12}>
                                    <ProviderSelectedRecord form={form} caseIdUUID={caseUUID} />
                                </Grid>
                            </Grid>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleCloseDialog}>Close</Button>
                            <Button variant={"contained"} onClick={form.handleSubmit(onSubmit)}>
                                {editPartyId ? "Edit" : "New"} Provider
                            </Button>
                        </DialogActions>
                    </form>
                </FormProvider>
            </Dialog>
        </>
    );
};

export default AddEditProviderDialog;
